import { useMutation, useQuery } from '@apollo/client';
import {
  DirectionalHint,
  IPanelHeaderRenderer,
  IPanelProps,
  IRenderFunction,
  makeStyles,
  Stack,
  Text,
  TooltipHost,
} from '@fluentui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { cutToast } from 'utility/toast';
import DraggablePanel from 'common/components/DraggablePanel';
import { PanelHeader } from 'common/components/PanelHeader';
import { UnsavedIndicator } from 'common/components/UnsavedIndicator';
import { useCommonStyles } from 'common/styles';
import { PanelCommonProps } from 'common/types/utility';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ApprovalSetupFlowCommonData } from 'settings/account/intuitiveApprovalSetupFlow/__generated__/ApprovalSetupFlowCommonData';
import { FlowApproval_Query } from 'settings/account/intuitiveApprovalSetupFlow/__generated__/FlowApproval';
import * as Yup from 'yup';
import { RunTest } from '../../RunTest';
import {
  ApprovalTestResult,
  ApprovalTestResultVariables,
} from '../../__generated__/ApprovalTestResult';
import {
  ApprovalTestResultCreate,
  ApprovalTestResultCreateVariables,
} from '../../__generated__/ApprovalTestResultCreate';
import { TEST_CASES_INITIAL_VALUES } from '../constants';
import { TestCaseValues } from '../types';
import { Footer } from './Footer';
import { ShimmerSection } from './shimmer';
import { getAddTestCaseVariables, getUpdateTestCaseVariables } from './util';

import APPROVAL_TYPE_COMMON_DATA from '../../../../../ApprovalSetupFlowCommonData.graphql';
import APPROVAL_TYPE_DATA from '../../ApprovalTestResult.graphql';
import APPROVAL_TEST_CREATE from '../../ApprovalTestResultCreate.graphql';
import APPROVAL_TEST_UPDATE from '../../ApprovalTestResultUpdate.graphql';
import {
  ApprovalTestResultUpdate,
  ApprovalTestResultUpdateVariables,
} from '../../__generated__/ApprovalTestResultUpdate';
import { DeleteTest } from '../../delete';
import { TestCaseFormContent } from './TestCaseFormContent';
import TEST_GROUPS from '../TestGroupsModal/ApprovalTestGroups.graphql';
import { ApprovalState } from 'common/constants';

export const validationSchema = () =>
  Yup.object({
    comment: Yup.string().nullable().required('Required'),
  });

export const FormView: React.FC<{
  panelVisible: boolean;
  onClose: () => void;
  onRefresh: () => void;
  onSubmitComplete: (id: string | null | undefined) => void;
  approvalDetailsData: FlowApproval_Query | undefined;
  testCaseSelected: string | undefined;
  setTestCaseSelected: React.Dispatch<React.SetStateAction<string | undefined>>;
}> = ({
  panelVisible,
  onClose,
  approvalDetailsData,
  onRefresh,
  onSubmitComplete,
  testCaseSelected,
  setTestCaseSelected,
}) => {
  const isNew = testCaseSelected === undefined;
  const commonStyles = useCommonStyles();
  const formMethods = useForm<TestCaseValues>({
    mode: 'all',
    resolver: yupResolver(validationSchema()),
  });

  const [addTestCase] = useMutation<
    ApprovalTestResultCreate,
    ApprovalTestResultCreateVariables
  >(APPROVAL_TEST_CREATE);
  const [updateTestCase, { loading: updateLoading }] = useMutation<
    ApprovalTestResultUpdate,
    ApprovalTestResultUpdateVariables
  >(APPROVAL_TEST_UPDATE);

  const { data: approvalTypes } = useQuery<ApprovalSetupFlowCommonData>(
    APPROVAL_TYPE_COMMON_DATA,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-only',
    }
  );

  const { data: approvalTestData, loading: approvalTestDataLoading } = useQuery<
    ApprovalTestResult,
    ApprovalTestResultVariables
  >(APPROVAL_TYPE_DATA, {
    skip: testCaseSelected === undefined,
    variables: { id: testCaseSelected! },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  const styles = useStyles();

  const onHandleSubmit = async (
    values: TestCaseValues,
    testGroupId: string | undefined,
    changeToCustomTest: boolean = false
  ) => {
    if (isNew) {
      const variables = getAddTestCaseVariables(
        values,
        approvalDetailsData,
        testGroupId
      );

      const { errors, data } = await addTestCase({ variables });

      if (errors?.length) cutToast.error(errors[0].message);
      else {
        onRefresh();
        onSubmitComplete(
          data?.approvalTestResultCreate?.approvalTestResult?.id
        );
        cutToast.success('Test case created successfully');
      }
    } else {
      if (approvalTestData?.approvalTestResult) {
        const variables = getUpdateTestCaseVariables(
          values,
          approvalTestData?.approvalTestResult,
          testGroupId,
          changeToCustomTest
        );
        const { errors } = await updateTestCase({
          variables,
          awaitRefetchQueries: true,
          refetchQueries: [
            {
              query: APPROVAL_TYPE_DATA,
              variables: {
                id: testCaseSelected,
              },
            },
            {
              query: TEST_GROUPS,
              variables: {
                filter: {
                  approvalId: { equalTo: approvalDetailsData?.approval?.id },
                },
                orderBy: '_FULL_COMMENT_ASC',
              },
            },
          ],
        });

        if (!errors) {
          cutToast.success('Test case is updated');
        } else cutToast.error(errors[0].message);
      }
    }
  };

  const _onRenderFooter: IRenderFunction<IPanelProps> = () => {
    return (
      <Footer
        isNew={isNew}
        isLoading={false}
        onCancel={() => onClose()}
        onSave={() => {
          formMethods.handleSubmit(
            (values) => onHandleSubmit(values, undefined),
            () => {
              // error handler
            }
          )();
        }}
        onCreate={() => {
          setTestCaseSelected(undefined);
        }}
        onGroupSelect={(groupId) => {
          formMethods.handleSubmit(
            (values) => onHandleSubmit(values, groupId),
            () => {
              // error handler
            }
          )();
        }}
        isCustomTest={!!approvalTestData?.approvalTestResult?._isCustomTest}
        onCustomTestSelect={() => {
          formMethods.handleSubmit(
            (values) => onHandleSubmit(values, undefined, true),
            () => {
              // error handler
            }
          )();
        }}
      />
    );
  };
  const comment = approvalTestData?.approvalTestResult?._fullComment;
  const displayHeader =
    comment?.length && comment.length > 21
      ? `${comment?.slice(0, 18)}...`
      : comment;
  //  Panel Header
  const _onRenderHeader: IPanelHeaderRenderer = () => {
    return (
      <PanelHeader
        hasHeaderText={false}
        onClose={() => {
          if (
            formMethods.formState.isDirty &&
            !formMethods.formState.isSubmitting
          ) {
            const confirm = window.confirm(
              'Are you sure you want to leave your changes unsaved?'
            );

            if (!confirm) return;
          }
          onClose();
        }}
      >
        <Stack grow horizontal horizontalAlign="space-between">
          <Stack horizontal tokens={{ childrenGap: 10 }}>
            <Text variant="xLarge">{isNew ? 'Create Test' : 'Edit Test'}</Text>
            {approvalTestData?.approvalTestResult && !isNew && comment && (
              <TooltipHost
                content={comment}
                directionalHint={DirectionalHint.topCenter}
              >
                <Text
                  variant="xLarge"
                  className={commonStyles.colorThemePrimary}
                >
                  {displayHeader}
                </Text>
              </TooltipHost>
            )}
          </Stack>
          <Stack style={{ marginLeft: 'auto', paddingRight: 10 }}>
            {approvalTestData?.approvalTestResult?._rowTimestamp &&
              approvalDetailsData?.approval?.id && (
                <DeleteTest
                  approvalTestResult={approvalTestData?.approvalTestResult}
                  onClose={onClose}
                  approvalId={approvalDetailsData?.approval?.id}
                />
              )}
          </Stack>

          {!formMethods.formState.isDirty && (
            <Stack horizontal>
              <RunTest
                onSuccess={() => {
                  onRefresh();
                }}
                allowGlobalTest={
                  approvalDetailsData?.approval?.statusType?.statusType.toUpperCase() ===
                    ApprovalState.APPROVED ||
                  approvalDetailsData?.approval?.isNewApprovalSelectable
                }
                approvalTestResultId={approvalTestData?.approvalTestResult?.id!}
                requestApprovalId={approvalDetailsData?.approval?.id!}
              />
              <UnsavedIndicator
                visible={
                  !isNew &&
                  !approvalTestDataLoading &&
                  formMethods.formState.isDirty &&
                  !formMethods.formState.isSubmitting
                }
              />
            </Stack>
          )}
        </Stack>
      </PanelHeader>
    );
  };

  useEffect(() => {
    if (!panelVisible) return;

    if (testCaseSelected && approvalTestData?.approvalTestResult) {
      formMethods.reset(approvalTestData.approvalTestResult);
      formMethods.trigger();
    } else if (!testCaseSelected) {
      formMethods.reset(TEST_CASES_INITIAL_VALUES);
      formMethods.trigger();
    }
  }, [
    testCaseSelected,
    panelVisible,
    formMethods,
    approvalTestData?.approvalTestResult,
  ]);

  return (
    <FormProvider {...formMethods}>
      <DraggablePanel
        {...PanelCommonProps}
        isOpen={panelVisible}
        initialWidth={1000}
        minWidth={1000}
        isBlocking={isNew}
        onRenderHeader={() => _onRenderHeader()}
        onRenderFooter={_onRenderFooter}
        isLightDismiss
      >
        {approvalTestDataLoading ? (
          <ShimmerSection />
        ) : (
          <TestCaseFormContent approvalTypes={approvalTypes} />
        )}
      </DraggablePanel>
    </FormProvider>
  );
};

export const useStyles = makeStyles((theme) => ({
  flexHalf: {
    flex: 0.5,
  },
  container: {
    width: '100%',
  },
}));
